import React, { useState, useRef, useEffect } from 'react';
import { Card, Tag, Typography, Divider, Popconfirm, Modal, Image, Space, Button, Empty } from 'antd';
import Icon, { LineChartOutlined, QrcodeOutlined, LockOutlined } from '@ant-design/icons';
import { DevicesType } from '@/api/devices';
import { generateDeviceQrcode } from '@/api/device';
import dayjs from 'dayjs';
import QRCode from 'qrcode';
import DeviceHistoryChart from './components/DeviceHistoryChart';
import './DeviceCard.less';
import { Area } from '@ant-design/plots';

import { ReactComponent as DirectDeviceIcon } from '@/assets/images/icon2.svg';
import { ReactComponent as GatewayDeviceIcon1 } from '@/assets/images/icon11.svg';
import { ReactComponent as GatewayDeviceIcon2 } from '@/assets/images/icon12.svg';
import { ReactComponent as GatewayDeviceIcon3 } from '@/assets/images/icon13.svg';

export interface DeviceCardProps {
  record: DevicesType;
  onDetail: (record: DevicesType) => void;
  onEdit: (record: DevicesType) => void;
  onDelete: (record: DevicesType) => void;
  checkPermissionDisabled: (permission: string) => boolean;
}

function Status({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    在线: '#52C41A',
    离线: 'rgba(0, 0, 0, 0.25)',
    告警中: '#FF4D4F',
    未激活: '#FAAD14',
    禁用: '#FF4D4F',
    锁止: '#FF4D4F',
  };
  const color = colorMap[status] || 'rgba(0, 0, 0, 0.25)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ background: color, width: 6, height: 6, borderRadius: 999 }}></div>
      <div style={{ color: 'rgba(0, 0, 0, 0.88)', opacity: 0.88 }}>{status}</div>
    </div>
  );
}

/** 激活状态标签 */
function ActivationBadge({ isActivated, deviceSn }: { isActivated?: boolean; deviceSn?: string }) {
  // 明确已激活
  if (isActivated === true) {
    return <Tag color="green" style={{ fontSize: 12 }}>已激活</Tag>;
  }
  // 明确未激活
  if (isActivated === false) {
    return <Tag icon={<LockOutlined />} color="orange" style={{ fontSize: 12 }}>未激活</Tag>;
  }
  // 列表API不返回is_activated，通过deviceSn推断
  if (deviceSn) {
    return <Tag color="green" style={{ fontSize: 12 }}>已激活</Tag>;
  }
  return <Tag icon={<LockOutlined />} color="orange" style={{ fontSize: 12 }}>未激活</Tag>;
}

const DeviceCard: React.FC<DeviceCardProps> = ({ record, onDetail, onEdit, onDelete, checkPermissionDisabled }) => {
  const [showChart, setShowChart] = useState(false);
  const [qrcodeModalOpen, setQrcodeModalOpen] = useState(false);
  const [qrcodeDataUrl, setQrcodeDataUrl] = useState('');
  const [deviceSn, setDeviceSn] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [loadingQrcode, setLoadingQrcode] = useState(false);
  const [qrcodeError, setQrcodeError] = useState('');

  /** 显示设备二维码 */
  const handleShowQrcode = async () => {
    setLoadingQrcode(true);
    setQrcodeModalOpen(true);
    setQrcodeDataUrl('');
    setQrcodeError('');
    try {
      // 如果 device_sn 为空，先自动写入
      const sn = record.device_sn || record.activation_code || record.id;
      if (!record.device_sn) {
        setDeviceSn(sn);
      }
      const resp = await generateDeviceQrcode(record.id);
      if (resp.success && resp.result) {
        const code = resp.result.activation_code || resp.result.device_sn;
        if (!code) {
          setQrcodeError('设备缺少编码信息，无法生成二维码');
          return;
        }
        setDeviceSn(resp.result.device_sn || sn);
        setActivationCode(code);
        // 前端生成二维码图片
        const dataUrl = await QRCode.toDataURL(code, {
          width: 260,
          margin: 2,
          color: { dark: '#000000', light: '#ffffff' },
        });
        setQrcodeDataUrl(dataUrl);
      } else {
        setQrcodeError(resp.errorMsg || '获取二维码数据失败');
      }
    } catch (err: any) {
      console.error('生成二维码失败:', err);
      setQrcodeError(err?.message || err?.msg || '网络请求失败，请检查后端服务是否已部署最新版本');
    } finally {
      setLoadingQrcode(false);
    }
  };

  const handleBottomClick = () => {
    setShowChart(!showChart);
  };

  return (
    <Card className="device-card" hoverable styles={{}}>
      <div>
        <div className="" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, overflow: 'hidden' }}>
          <div style={{ flex: 'auto', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            {/* {record.image_url ? (
              <img src={record.image_url} style={{ width: 63, height: 63, flex: 'none', display: 'block', marginRight: 16 }}></img>
            ) : (
              <Icon component={DirectDeviceIcon} style={{ fontSize: 63, width: 63, height: 63, flex: 'none', display: 'block', marginRight: 16 }}></Icon>
            )} */}
            <Icon
              component={record.node_type === '网关设备' ? GatewayDeviceIcon2 : record.node_type === '摄像头' ? GatewayDeviceIcon1 : GatewayDeviceIcon3}
              style={{ fontSize: 63, width: 63, height: 63, flex: 'none', display: 'block', marginRight: 16 }}
            ></Icon>

            <div style={{ flex: 'auto', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 4 }}>
                <div style={{ fontSize: 16, color: '#3D3D3D', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} onClick={() => onDetail(record)}>
                  {/* {record.node_type} */}
                  {record.name}
                </div>

                <div style={{ display: 'flex', gap: 8, justifySelf: 'flex-end', flex: 'none', alignSelf: 'self-start' }}>
                  <div
                    style={{ height: 24, borderRadius: 9999, lineHeight: '22px', border: '1px solid #1A1A1A', width: 45, textAlign: 'center', cursor: 'pointer' }}
                    onClick={() => {
                      onDetail(record);
                    }}
                  >
                    详情
                  </div>
                  <div
                    style={{ height: 24, borderRadius: 9999, lineHeight: '22px', border: '1px solid #0256FF', color: '#0256FF', width: 45, textAlign: 'center', cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShowQrcode();
                    }}
                    title="查看设备二维码"
                  >
                    <QrcodeOutlined style={{ fontSize: 12 }} />
                  </div>
                  <Popconfirm
                    title="此操作将永久删除该条记录, 是否继续?"
                    okText="确定"
                    cancelText="取消"
                    placement="topRight"
                    disabled={checkPermissionDisabled('gateway:device:remove')}
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      onDelete(record);
                    }}
                  >
                    <div style={{ height: 24, borderRadius: 9999, lineHeight: '22px', border: '1px solid #1A1A1A', width: 45, textAlign: 'center' }}>删除</div>
                  </Popconfirm>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Status status={record.status} />
                <ActivationBadge isActivated={record.is_activated} deviceSn={record.device_sn} />
                {record.is_alert && <Status status={'告警中'} />}
              </div>
            </div>
          </div>
        </div>
        <div style={{ width: 'calc(100% + 40px)', marginLeft: -20, paddingTop: 10 }}>
          <Area
            data={record.daily_device_msg_count || []}
            height={80}
            style={{}}
            tooltip={{
              formatter: (datum) => {
                return { name: '消息数', value: `${datum.count}条` };
              },
            }}
            {...{
              xField: 'date',
              yField: 'count',
              smooth: true,
              point: {
                size: 0,
              },
              line: {
                color: '#0256FF',
              },
              color: '#0256FF',
              areaStyle: () => {
                return {
                  fill: 'l(0) 0:#765DFF 1:#CFCAFF',
                };
              },
              xAxis: {
                // tickLine: false,
                line: null,
                grid: null,
                label: null,
              },
              yAxis: {
                line: null,
                grid: null,
                label: null,
              },
            }}
          />
        </div>
      </div>

      {/* 二维码弹窗 */}
      <Modal
        title="设备二维码"
        open={qrcodeModalOpen}
        onCancel={() => setQrcodeModalOpen(false)}
        footer={null}
        width={400}
        centered
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          {loadingQrcode ? (
            <div style={{ padding: 40 }}>加载中...</div>
          ) : qrcodeDataUrl ? (
            <>
              <Image
                src={qrcodeDataUrl}
                alt="设备二维码"
                style={{ width: 240, height: 240, margin: '0 auto' }}
                preview={false}
              />
              <div style={{ marginTop: 16, fontSize: 14, color: '#666' }}>
                <div>设备名称：<strong>{record.name}</strong></div>
                <div style={{ marginTop: 8 }}>设备编码：<Typography.Text style={{ fontSize: 13 }} copyable>{deviceSn || record.device_sn || record.id || '-'}</Typography.Text></div>
                <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>
                  请将此二维码粘贴到设备上，服务商扫码后可激活
                </div>
              </div>
            </>
          ) : qrcodeError ? (
            <Empty description={
              <span style={{ color: '#ff4d4f' }}>
                {qrcodeError}
              </span>
            } />
          ) : (
            <Empty description="暂无二维码数据" />
          )}
          <Space style={{ marginTop: 16 }}>
            <Button size="small" onClick={() => {
              if (qrcodeDataUrl) {
                const img = new window.Image();
                img.src = qrcodeDataUrl;
                img.onload = () => {
                  const canvas = document.createElement('canvas');
                  const padding = 20;
                  const textHeight = 40;
                  const sn = deviceSn || record.device_sn || record.id || '';
                  canvas.width = img.width + padding * 2;
                  canvas.height = img.height + padding * 2 + textHeight + 24;
                  const ctx = canvas.getContext('2d');
                  ctx.fillStyle = '#ffffff';
                  ctx.fillRect(0, 0, canvas.width, canvas.height);
                  ctx.drawImage(img, padding, padding + 12, img.width, img.height);
                  ctx.fillStyle = '#333333';
                  ctx.font = 'bold 16px sans-serif';
                  ctx.textAlign = 'center';
                  ctx.fillText(record.name || '', canvas.width / 2, padding + 6);
                  ctx.font = '13px sans-serif';
                  ctx.fillStyle = '#666666';
                  ctx.fillText('设备编码: ' + sn, canvas.width / 2, img.height + padding + 38);
                  const link = document.createElement('a');
                  link.href = canvas.toDataURL('image/png');
                  link.download = 'qrcode_' + sn + '.png';
                  link.click();
                };
              }
            }} disabled={!qrcodeDataUrl}>下载二维码</Button>
          </Space>
        </div>
      </Modal>
    </Card>
  );
  // return (
  //   <Card className="device-card" hoverable>
  //     <div className="device-card__content">
  //       <div className="device-card__main">
  //         <div className="device-card__top">
  //           <div className="device-card__left">
  //             <div className="device-card__title">{record.name}</div>
  //             <div className="device-card__time">{record.created ? dayjs(record.created).format('YYYY-MM-DD HH:mm:ss') : '-'}</div>
  //             <div className="device-card__tags">
  //               {/* <Tag bordered={false} color={getStatusColor(record.status)}>{record.status || '未知'}</Tag> */}
  //               <Tag bordered={false} color="geekblue">
  //                 {record.node_type || '未知类型'}
  //               </Tag>
  //               {/* {record.driver_service_name && <Tag bordered={false} color="default">{record.driver_service_name}</Tag>} */}
  //             </div>
  //           </div>
  //           <div className="device-card__right">
  //             <div className="device-card__image">
  //               <img
  //                 src={record.image_url || require('@/assets/images/default.32752796.png')}
  //                 alt={record.name}
  //                 onError={(e) => {
  //                   (e.target as HTMLImageElement).src = require('@/assets/images/default.32752796.png');
  //                 }}
  //               />
  //             </div>
  //           </div>
  //         </div>
  //         <div className="device-card__bottom">
  //           {showChart ? (
  //             <div className="device-card__chart">
  //               <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
  //                 <LineChartOutlined
  //                   style={{
  //                     fontSize: 16,
  //                     color: '#1890ff',
  //                     cursor: 'pointer',
  //                   }}
  //                   onClick={handleBottomClick}
  //                 />
  //               </div>
  //               {record.id && <DeviceHistoryChart key={record.id} deviceId={record.id} />}
  //             </div>
  //           ) : (
  //             <div className="device-card__stats">
  //               <div className="device-card__stat-item">
  //                 <div className="device-card__stat-label">属性数量</div>
  //                 <div className="device-card__stat-value">{record?.property_report_count ?? '0'}</div>
  //               </div>
  //               <div className="device-card__stat-item">
  //                 <div className="device-card__stat-label">事件数量</div>
  //                 <div className="device-card__stat-value">{record?.event_report_count ?? '0'}</div>
  //               </div>
  //               <div className="device-card__stat-item">
  //                 <div className="device-card__stat-label">控制数量</div>
  //                 <div className="device-card__stat-value">{record?.property_set_count ?? '0'}</div>
  //               </div>
  //               <div className="device-card__stat-item">
  //                 <div className="device-card__stat-label" style={{ visibility: 'hidden' }}>
  //                   占位
  //                 </div>
  //                 <div className="device-card__stat-value">
  //                   <LineChartOutlined
  //                     style={{
  //                       fontSize: 16,
  //                       color: '#1890ff',
  //                       cursor: 'pointer',
  //                     }}
  //                     onClick={handleBottomClick}
  //                   />
  //                 </div>
  //               </div>
  //             </div>
  //           )}
  //         </div>
  //       </div>
  //       <Divider className="device-card__divider" />
  //       <div className="device-card__actions">
  //         <Typography.Link
  //           className="device-card__action-link"
  //           onClick={(e) => {
  //             e.stopPropagation();
  //             onDetail(record);
  //           }}
  //         >
  //           详情
  //         </Typography.Link>
  //         <Divider type="vertical" className="device-card__action-divider" />
  //         <Typography.Link
  //           className="device-card__action-link"
  //           disabled={checkPermissionDisabled('gateway:device:edit')}
  //           onClick={(e) => {
  //             e.stopPropagation();
  //             onEdit(record);
  //           }}
  //         >
  //           编辑
  //         </Typography.Link>
  //         <Divider type="vertical" className="device-card__action-divider" />
  //         <Popconfirm
  //           title="此操作将永久删除该条记录, 是否继续?"
  //           okText="确定"
  //           cancelText="取消"
  //           placement="topRight"
  //           disabled={checkPermissionDisabled('gateway:device:remove')}
  //           onConfirm={(e) => {
  //             e?.stopPropagation();
  //             onDelete(record);
  //           }}
  //         >
  //           <Typography.Link
  //             className="device-card__action-link"
  //             disabled={checkPermissionDisabled('gateway:device:remove')}
  //             onClick={(e) => {
  //               e.stopPropagation();
  //             }}
  //           >
  //             删除
  //           </Typography.Link>
  //         </Popconfirm>
  //       </div>
  //     </div>
  //   </Card>
  // );
};

export default DeviceCard;
